import { useTranslation } from 'react-i18next'
import { Megaphone } from 'lucide-react'
import { detectPlatformAdapter } from '../../platform'
import { useSession } from '../../store/user.store'

/**
 * Compact card inviting the user to subscribe to Yadviga's content
 * channel for their language. Lives on /profile right under
 * PremiumStatusCard. Tap → adapter.openLink('t.me/yadviga_<lang>')
 * which on Telegram opens the channel inside the same client and on
 * MAX/web falls through to a regular link.
 *
 * Handles are hard-coded — these are our owned channels, not a
 * per-deployment config knob. If we ever fork to a sibling product
 * the constants below get edited.
 */
const CHANNEL_BY_LANG: Record<'ru' | 'en', string> = {
  ru: 'yadviga_ru',
  en: 'yadviga_en',
}

export function ChannelSubscribeCard() {
  const { t } = useTranslation()
  const user = useSession((s) => s.user)
  const language = (user?.language ?? 'ru') as 'ru' | 'en'
  const handle = CHANNEL_BY_LANG[language]

  const openChannel = () => {
    const adapter = detectPlatformAdapter()
    void adapter.openLink(`https://t.me/${handle}`)
  }

  return (
    <button
      type="button"
      onClick={openChannel}
      className="flex w-full items-center gap-3 rounded-2xl bg-gradient-to-br from-[#fbeadd] to-[#f5d9c0] p-4 text-left ring-1 ring-[#e6c4a4]/60 transition active:scale-[0.99]"
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/70 text-copper">
        <Megaphone className="h-5 w-5" strokeWidth={1.8} aria-hidden />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-serif text-sm text-sage-800 leading-snug">
          {t('channel.subscribe_title')}
        </p>
        <p className="mt-0.5 text-xs text-sage-600">
          {t('channel.subscribe_desc', { handle: `@${handle}` })}
        </p>
      </div>
    </button>
  )
}
